import { NavLink } from 'react-router-dom';
import { useIsStuck } from '../../lib/use-is-stuck';
import { STICKY_OFFSET_BELOW_TOPBAR } from '../../lib/layout-offsets';
import { cn } from '../../lib/utils';
import { V2_PLACEHOLDER_PROPS } from '../../lib/v2-deferred';
import { CEW_REPORT_PDF_URL } from '../../lib/cew-site';
import { PdfDownloadButton } from '../shared/PdfDownloadButton';
import { ACTIVE_BORDER, ACTIVE_TEXT, HOVER_CLASS, TABS } from './section-nav-tabs';

interface TabIconProps {
  readonly children: React.ReactNode;
  readonly isActive: boolean;
}

function TabIcon({ children, isActive }: TabIconProps) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('transition-opacity', isActive ? 'opacity-100' : 'opacity-50')}
    >
      {children}
    </svg>
  );
}

/**
 * The 6-tab horizontal section nav that sits sticky just below the Topbar
 * (top: 56px). Per-tab color treatment matches `_archive/home.html` lines
 * 282–289 + 738–754: active = colored text + 3px colored bottom border on
 * a transparent bg, hover = colored text + tab-tinted bg, idle = neutral
 * subtle text, disabled = greyed at 20% opacity. When scrolled under the
 * topbar, picks up an `is-stuck` shadow.
 *
 * Report affordance (CR-027/028/029/030, 2026-08-18): re-homed here from
 * `Topbar` (see its doc note). CR-034 (Frank, 2026-08-28): it is now the
 * "Download Full Report" PDF pill, rendered via the shared
 * `<PdfDownloadButton variant="primary">` (DEC-044: three callers — this
 * strip, the mobile drawer, the homepage rail). Visible only at
 * `min-[1136px]` (matches desktop `CewSiteNav`'s floor). A real flex sibling of the
 * tab `<ul>` (not absolutely positioned — a geometry probe measured the
 * tab row at 870px, so a `right-6` overlay collided with "PROVIDERS" at
 * 1136-1280px), so the browser reserves the right room automatically.
 *
 * Tab-strip overflow (3rd review pass 2026-08-18): the tab `<ul>` is
 * the scroll owner (`overflow-x-auto` + `min-w-0`, NOT `shrink-0` — a
 * shrink-0 flex item never shrinks below its content width, making
 * overflow-x-auto inert) so at 600-893px it scrolls to reveal
 * "Providers" instead of clipping it off-screen.
 *
 * Centering without `justify-center` (4th review pass 2026-08-18):
 * `justify-center` on an OVERFLOWING flex container shifts the line's
 * START inward, pushing the first tab off-screen. Centering instead
 * comes from `ml-auto`/`mr-auto` on the first/last `<li>`, which
 * collapse to 0 under overflow (strip starts flush-left, "Home" stays
 * reachable) but still center the row once every tab fits.
 */
export function SectionNav() {
  const [navRef, isStuck] = useIsStuck<HTMLElement>(STICKY_OFFSET_BELOW_TOPBAR);
  return (
    <nav
      ref={navRef}
      aria-label="Page sections"
      className={cn(
        // Hidden on mobile (< 600px) — the Topbar's hamburger +
        // MobileMenuDrawer takes over as the nav surface there. On
        // desktop (600px+) the original sticky horizontal 6-tab strip
        // shows as before.
        'sticky top-[var(--h-topbar)] z-[350] hidden border-b border-border bg-white px-6 transition-shadow min-[600px]:flex',
        isStuck && 'shadow-sticky',
      )}
    >
      <ul className="flex min-w-0 grow overflow-x-auto">
        {TABS.map((tab, idx) => (
          <li
            key={tab.id}
            className={cn(idx === 0 && 'ml-auto', idx === TABS.length - 1 && 'mr-auto')}
          >
            {tab.to ? (
              <NavLink
                to={tab.to}
                end
                className={({ isActive }) =>
                  cn(
                    'flex h-[60px] min-w-[90px] items-center justify-center gap-2 px-8',
                    'border-b-[3px] -mb-px text-xs uppercase tracking-[0.04em] transition',
                    isActive
                      ? cn(ACTIVE_TEXT[tab.id], ACTIVE_BORDER[tab.id], 'font-bold bg-transparent')
                      : cn(
                          'border-b-transparent bg-transparent font-semibold text-text-subtle',
                          HOVER_CLASS[tab.id],
                        ),
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <TabIcon isActive={isActive}>{tab.icon}</TabIcon>
                    {tab.label}
                  </>
                )}
              </NavLink>
            ) : (
              <span
                {...V2_PLACEHOLDER_PROPS}
                className="flex h-[60px] min-w-[90px] cursor-not-allowed items-center justify-center gap-2 border-b-[3px] border-b-transparent -mb-px px-8 text-xs font-semibold uppercase tracking-[0.04em] text-text-3/20"
              >
                <TabIcon isActive={false}>{tab.icon}</TabIcon>
                {tab.label}
              </span>
            )}
          </li>
        ))}
      </ul>

      <PdfDownloadButton
        href={CEW_REPORT_PDF_URL}
        label="Download Full Report"
        variant="primary"
        density="compact"
        className="ml-2 hidden shrink-0 self-center min-[1136px]:inline-flex min-[1280px]:ml-4"
      />
    </nav>
  );
}
